import { CostDimension } from "../../store/apis/costAnalysisApi";
import { ENGAGEMENT_LABEL } from "../commercial-dashboard/dashboardFormat";

/** Nombre de cada dimensión en pantalla. */
export const DIMENSION_LABEL: Record<CostDimension, string> = {
  month: "Mes",
  platform: "Plataforma",
  technicalPlatform: "Plataforma técnica",
  campaignType: "Tipo de campaña",
  campaignFormat: "Formato",
  objective: "Objetivo",
  funnel: "Funnel",
  sector: "Sector",
  clientType: "Tipo de cliente",
  briefingType: "Tipo de briefing",
  investmentPayment: "Pago de la inversión",
  company: "Cliente",
};

/** Las que se ofrecen arriba; el resto, en "Más dimensiones". */
export const MAIN_DIMENSIONS: CostDimension[] = [
  "platform",
  "campaignType",
  "objective",
  "sector",
  "clientType",
  "month",
];

export const EXTRA_DIMENSIONS: CostDimension[] = [
  "technicalPlatform",
  "campaignFormat",
  "funnel",
  "briefingType",
  "investmentPayment",
  "company",
];

const BRIEFING_TYPE_LABEL: Record<string, string> = {
  PAID_MEDIA_MANAGED: "Paid media gestionado",
  BUY_MEDIA_DIRECT_AND_OWN: "Compra de medios",
};

const PAYMENT_LABEL: Record<string, string> = {
  CLIENTE: "Cliente",
  TI_MEDIA_GROUP_SL: "TI Media Group",
};

const FUNNEL_LABEL: Record<string, string> = {
  AWARENESS: "Awareness",
  CONSIDERATION: "Consideración",
  PERFORMANCE: "Performance",
};

const NONE_LABEL: Partial<Record<CostDimension, string>> = {
  sector: "Sin sector",
  clientType: "Sin tipo",
  technicalPlatform: "Compra directa",
  campaignType: "Sin tipo",
};

const MONTHS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

/** "2026-09" → "sep 2026" */
export const monthLabel = (value: string): string => {
  const [year, month] = value.split("-");
  return `${MONTHS[Number(month) - 1] ?? month} ${year}`;
};

/** Los enums tal cual vienen, sin guiones bajos: DYNAMIC_PLUS_IABA → DYNAMIC PLUS IABA. */
const enumLabel = (value: string): string => value.replace(/_/g, " ");

/** Texto de un valor de dimensión. El cliente se resuelve con su nombre aparte. */
export const valueLabel = (
  dimension: CostDimension,
  value: string | null | undefined,
  companyNames?: Map<string, string>
): string => {
  if (value === null || value === undefined || value === "") {
    return NONE_LABEL[dimension] ?? "Sin dato";
  }
  switch (dimension) {
    case "month":
      return monthLabel(value);
    case "clientType":
      return ENGAGEMENT_LABEL[value] ?? value;
    case "briefingType":
      return BRIEFING_TYPE_LABEL[value] ?? enumLabel(value);
    case "investmentPayment":
      return PAYMENT_LABEL[value] ?? enumLabel(value);
    case "funnel":
      return FUNNEL_LABEL[value] ?? enumLabel(value);
    case "company":
      return companyNames?.get(value) ?? value;
    case "sector":
      return value;
    default:
      return enumLabel(value);
  }
};

/** Qué cuenta como volumen en cada objetivo (lo mismo que el WIP). */
export const VOLUME_LABEL: Record<string, string> = {
  CPM: "impresiones",
  CPMV: "vistas completas",
  CPV: "vistas completas",
  CPC: "clics",
  CPA: "conversiones",
  CPL: "conversiones",
  CPE: "interacciones",
};

const eur = (decimals: number) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
const EUR0 = eur(0);
const EUR2 = eur(2);
const EUR4 = eur(4);

/** Importes: sin decimales. */
export const money = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(n) ? "—" : EUR0.format(n);

/** Costes unitarios: 2 decimales desde 1 €, 4 por debajo (un CPC de 0,0345 €). */
export const unitMoney = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return Math.abs(n) >= 1 ? EUR2.format(n) : EUR4.format(n);
};

export const pct = (n: number | null | undefined, decimals = 1): string =>
  n === null || n === undefined || !Number.isFinite(n)
    ? "—"
    : `${n.toLocaleString("es-ES", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} %`;

/** 1.234.567 → "1,2 M"; por debajo de 10.000, el número entero. */
export const compact = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  if (Math.abs(n) < 10_000) return Math.round(n).toLocaleString("es-ES");
  return new Intl.NumberFormat("es-ES", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
};

/** YYYY-MM de hoy menos `monthsBack` meses. */
export const monthOffset = (monthsBack: number): string => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - monthsBack);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
